/**
 * RSS feed adapter — rss.feed, provider 'user', tier free.
 *
 * CLIENT-ONLY adapter (ADR-005 / ADR-004): feed URL is user-supplied, fetched
 * browser-direct. NEVER hits our Lambda (rss.feed is absent from handler
 * PARAM_SCHEMAS — server-side proxy is deliberately blocked).
 *
 * CLIENT / SERVER SPLIT (ADR-002 §5):
 *   All members are CLIENT-SIDE. No buildRequest/responseSchema/normalize —
 *   this adapter uses ClientAdapter<Params, Data>, not ProviderAdapter.
 *
 * Glance grammar rulings (glance-grammar-spec §1.1, user-source row):
 *
 * primary (≤8 chars):
 *   Newest item title, sliced to 8 chars.
 *   No items: 'No items' (exactly 8 chars, calm).
 *   Ruling: mirrors news.headlines — first 8 chars of the headline is the
 *   "kicker" pattern. 'No items' is informational, not an alarm.
 *
 * delta (≤20 chars):
 *   Feed source/hostname (sliced to 20). Always shown — even with no items,
 *   the source identifies which feed this tile represents.
 *   Ruling: delta=null would leave a blank tile with no identification; showing
 *   the source is more useful for a multi-feed board.
 *
 * state: always null (neutral) — feeds are informational; no live/alert needed.
 *
 * Failure state mapping (ADR-005 CORS-honesty principle):
 *   The caller (use-tile-view-model) handles failure states and sets the glance
 *   vm directly; the adapter only handles the ok path.
 *
 *   cors    → delta "Can't access"  (honest: "this feed doesn't allow direct access")
 *   timeout → delta "Feed timed out"
 *   invalid → delta "Not a valid feed"
 *   error   → delta "Feed error"
 *   All failures: dataState 'error', glanceState 'stale', primary ''
 *
 * Immutability: adapter object is frozen; glanceView returns new object.
 */
import { z } from 'zod';
import {
  rssParamsSchema,
  deriveCacheKey,
  type RssParams,
} from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { ClientAdapter, GlanceViewModel } from 'briefpane/contracts';
import type { RssFeedData } from './fetch-feed.js';

/* -------------------------------------------------------------------------- */
/* Data shape schema — mirrors DATA_SHAPE_KEYS['rss.feed']                    */
/* (headline, source, publishedAt, itemCount) — mirrors news.headlines         */
/* -------------------------------------------------------------------------- */

export const rssDataShape = z.object({
  headline: z.string(),
  source: z.string(),
  publishedAt: z.string(),
  itemCount: z.number().int().min(0),
  // items is an array of RssItem objects; we use z.unknown() here to avoid
  // exactOptionalPropertyTypes conflicts with the link?: string field.
  // The fetch-feed module enforces item shape at parse time.
  items: z.array(z.unknown()),
}) as z.ZodType<RssFeedData>;

/* -------------------------------------------------------------------------- */
/* Glance view                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Map RssFeedData + GlanceMap → GlanceViewModel.
 *
 * primary: headline phrase (≤ 80-char safety bound, D-018 — the renderer's CSS
 *          ellipsis handles visual fit), or 'No items' when feed is empty.
 * delta:   feed source (hostname or title), sliced to 20 chars.
 * state:   always 'neutral' (informational, not urgent).
 *
 * Pure. Returns a new object every call.
 */
export function rssGlanceView(data: RssFeedData, _glance: GlanceMap): GlanceViewModel {
  const primary = data.headline ? data.headline.slice(0, 80) : 'No items';
  const delta = data.source.slice(0, 20) || null;

  return {
    dataState: 'ok',
    glanceState: 'neutral',
    slots: { primary, delta },
    label: null,
    fetchedAt: Date.now(),
  };
}

/* -------------------------------------------------------------------------- */
/* Adapter                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The RSS feed adapter.
 *
 * Satisfies ClientAdapter<RssParams, RssFeedData> — the client-only projection.
 * Server-only members (buildRequest, responseSchema, normalize) are absent because
 * rss.feed is user-source + clientDirect and fetches browser → feedUrl directly
 * (ADR-005). The Lambda proxy is deliberately excluded.
 */
export const rssAdapter: ClientAdapter<RssParams, RssFeedData> = Object.freeze({
  type: 'rss.feed' as const,
  provider: 'user' as const,
  tier: 'free' as const,
  ttlSec: 600,
  paramsSchema: rssParamsSchema,
  dataShape: rssDataShape,

  cacheKey: (params: RssParams): string =>
    deriveCacheKey({ type: 'rss.feed', provider: 'user', params }),

  glanceView: rssGlanceView,
});
